import { gql } from "@apollo/client";
import { alpha, ButtonBase, CircularProgress, darken } from "@mui/material";
import { COLOR_BLACK, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING_HALF } from "helpers/spacings";
import useActionFragment from "helpers/useActionFragment";
import useShowMessage from "helpers/useShowMessage";
import { CheckCircleOutline, Creation } from "mdi-material-ui";
import React, { useEffect, useState } from "react";

export const GameOverviewAiStatsFragment = gql`
  fragment GameOverviewAiStatsFragment on Game {
    id
    aiStatsAvailable
    aiStatsStatus
    aiStatsRequestedByCurrentUser
  }
`;

// Yellow invites, orange is working, green is done. Held here rather than in
// helpers/colors because they are this pill's traffic-light vocabulary and not
// part of the wider palette.
const COLOR_AI_ACTION = "#E2FF57";
const COLOR_AI_PROCESSING = "#EF8B26";
const COLOR_AI_DONE = "#35A05B";

// A single pill between the score and the video, saying where this game is up
// to. It replaces the button-plus-explainer split: one control, one line, and
// the same shape at every viewport width.
export default function GameOverviewAiStats({ game }) {
  const [submitting, submittingSet] = useState(false);
  const aiStatsRequestCreate = useActionFragment("aiStatsRequestCreate", "redirectUrl game { id aiStatsStatus }");
  const showMessage = useShowMessage();

  const status = game?.aiStatsStatus;

  // Coming back with the browser's back button restores this page from the
  // bfcache with its React state intact, so the pill would still be sitting in
  // "Taking you to Preciser…" for a navigation that is over. `pageshow` fires on
  // a bfcache restore where `load` does not, so it is the only reliable signal.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) submittingSet(false);
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (!game || status === "unavailable") {
    return null;
  }

  // Same tab rather than a popup: most users are on mobile, and in the app web
  // views some leagues embed us in, window.open silently does nothing. The
  // token carries a return_url back to this exact page, so leaving costs the
  // user nothing.
  const onClick = async () => {
    submittingSet(true);
    try {
      const result = await aiStatsRequestCreate({
        input: { gameId: game.id, returnUrl: window.location.href },
      });
      // useAction hands back the whole mutation payload, so the fields are one
      // level down under the mutation name.
      const url = result?.aiStatsRequestCreate?.redirectUrl;
      if (!url) throw new Error("No redirect URL returned");

      // Left deliberately submitting: the navigation is about to replace the
      // page, and flipping the pill back to its idle state first reads as if
      // nothing happened.
      window.location.assign(url);
    } catch (e) {
      // Stay put after a failed submit — the provider's page would show a
      // purchase screen for a request that never landed.
      //
      // useAction has already shown the real GraphQL error and then thrown an
      // AbortError purely to unwind us. Showing that too would put a second
      // toast reading "Cancelled" on top of the actual reason.
      if (!e?.isAbortError) {
        showMessage(e?.message || "Could not start AI stats. Please try again.");
      }
      submittingSet(false);
    }
  };

  const pill = pillFor({ status, submitting });

  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: SPACING_HALF, width: "100%" }}>
      <ButtonBase
        onClick={pill.action ? onClick : undefined}
        disabled={!pill.action || submitting}
        disableRipple={!pill.action}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "9px 22px",
          borderRadius: "999px",
          backgroundColor: pill.background,
          color: pill.color,
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: 1.2,
          textAlign: "center",
          maxWidth: "100%",
          // Only the actionable state glows. A status has nothing to invite.
          boxShadow: pill.action ? `0 0 20px ${alpha(pill.background, 0.5)}` : "none",
          transition: "background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
          // A status pill is not a control, so it must not read as disabled —
          // it keeps its colour and its default cursor.
          "&.Mui-disabled": { color: pill.color, opacity: 1 },
          // Hover belongs only to the pill you can actually press. Putting it on
          // the status pills would advertise a click that does nothing.
          ...(pill.action && {
            "&:hover": {
              backgroundColor: darken(pill.background, 0.12),
              boxShadow: `0 0 26px ${alpha(pill.background, 0.75)}`,
              transform: "translateY(-1px)",
            },
            "&:active": { transform: "translateY(0)" },
          }),
        }}
      >
        {pill.icon}
        <span>{pill.text}</span>
      </ButtonBase>
    </div>
  );
}

function pillFor({ status, submitting }) {
  if (submitting) {
    return {
      background: COLOR_AI_PROCESSING,
      color: COLOR_WHITE_LIGHTER,
      icon: <CircularProgress size={16} thickness={5} style={{ color: COLOR_WHITE_LIGHTER }} />,
      text: "Taking you to Preciser…",
      action: false,
    };
  }

  // Already imported. Everyone sees it, and it is what stops a second person
  // paying for a game that has already been through AI.
  if (status === "completed") {
    return {
      background: COLOR_AI_DONE,
      color: COLOR_WHITE_LIGHTER,
      icon: <CheckCircleOutline style={{ fontSize: 18 }} />,
      text: "Game Includes AI Stats",
      action: false,
    };
  }

  // Waiting on the requester to finish the provider's form. Actionable, so it
  // keeps the inviting colour rather than the working one.
  if (status === "info_required") {
    return {
      background: COLOR_AI_ACTION,
      color: COLOR_BLACK,
      icon: <Creation style={{ fontSize: 18 }} />,
      text: "Finish AI Stats Setup",
      action: true,
    };
  }

  // The requester's last attempt did not land — our submit errored, or they were out of credits and
  // have just come back from the purchase screen. Per the partner guide §5 a top-up is not the end of
  // it: the video has to be submitted again from our side, and this is the control that does it.
  if (status === "failed") {
    return {
      background: COLOR_AI_ACTION,
      color: COLOR_BLACK,
      icon: <Creation style={{ fontSize: 18 }} />,
      text: "Retry AI Stats",
      action: true,
    };
  }

  if (status === "in_progress") {
    return {
      background: COLOR_AI_PROCESSING,
      color: COLOR_WHITE_LIGHTER,
      icon: <CircularProgress size={16} thickness={5} style={{ color: COLOR_WHITE_LIGHTER }} />,
      text: "AI Stats Processing",
      action: false,
    };
  }

  return {
    background: COLOR_AI_ACTION,
    color: COLOR_BLACK,
    icon: <Creation style={{ fontSize: 18 }} />,
    text: "Get AI Stats for this Game",
    action: true,
  };
}
